// src/Dashboard/pages/user/Produits/hooks/useProductsDashboard.js

import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";

import {
  DEFAULT_PRODUCTS_UI,
  filterAndSortProducts,
  getProductsShareUrl,
  PRODUCTS_API_URL,
} from "../utils/productsUtils";

const STORAGE_KEY = "products_dashboard_ui_state";

const getInitialUi = () => {
  let stored = {};

  try {
    stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    stored = {};
  }

  const params = new URLSearchParams(window.location.search);

  const urlState = {
    q: params.get("q") || undefined,
    sort: params.get("sort") || undefined,
  };

  return {
    ...DEFAULT_PRODUCTS_UI,
    ...stored,
    ...Object.fromEntries(
      Object.entries(urlState).filter(([, value]) => value !== undefined)
    ),
  };
};

export default function useProductsDashboard() {
  const [products, setProducts] = useState([]);
  const [ui, setUi] = useState(getInitialUi);
  const [debouncedQ, setDebouncedQ] = useState(ui.q || "");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [shareCopied, setShareCopied] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const persistUi = useCallback((nextUi) => {
    setUi(nextUi);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(nextUi));
  }, []);

  const updateUi = useCallback(
    (patch) => {
      persistUi({
        ...ui,
        ...patch,
      });
    },
    [persistUi, ui]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      persistUi({
        ...ui,
        q: debouncedQ,
      });
    }, 300);

    return () => clearTimeout(timer);
  }, [debouncedQ]);

  const refreshProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const response = await axios.get(PRODUCTS_API_URL);
      const data = response.data;

      if (!Array.isArray(data)) {
        throw new Error("Réponse API invalide.");
      }

      setProducts(data);
    } catch (err) {
      console.error("Erreur chargement applications :", err);
      setError("Impossible de charger les applications pour le moment.");
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshProducts();
  }, [refreshProducts]);

  const filteredProducts = useMemo(() => {
    return filterAndSortProducts(products, ui);
  }, [products, ui]);

  const resetFilters = useCallback(() => {
    setDebouncedQ("");
    persistUi(DEFAULT_PRODUCTS_UI);
  }, [persistUi]);

  const copyShareLink = useCallback(async () => {
    try {
      const url = getProductsShareUrl(ui);
      await navigator.clipboard.writeText(url);
      setShareCopied(true);

      setTimeout(() => {
        setShareCopied(false);
      }, 1600);
    } catch {
      setShareCopied(false);
    }
  }, [ui]);

  const openProductDrawer = useCallback((product) => {
    setSelectedProduct(product);
  }, []);

  const closeProductDrawer = useCallback(() => {
    setSelectedProduct(null);
  }, []);

  return {
    products,
    filteredProducts,
    loading,
    error,
    ui,
    debouncedQ,
    setDebouncedQ,
    updateUi,
    resetFilters,
    refreshProducts,
    shareCopied,
    copyShareLink,
    selectedProduct,
    openProductDrawer,
    closeProductDrawer,
  };
}